<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
      <h1><v-icon color="primary" large>mdi-weight-lifter</v-icon> Miembros</h1>
      <v-chip v-if="esAdmin" color="info" small>
        <v-icon left x-small>mdi-eye</v-icon> Vista Admin (con trazabilidad)
      </v-chip>
    </div>

    <v-data-table
      :loading="cargando"
      :headers="encabezadoTabla"
      :items="miembros"
      item-key="matricula"
      show-expand
      class="elevation-3 rounded-xl"
      :footer-props="{ itemsPerPageText: 'Por página' }"
    >
      <!-- Imagen -->
      <template v-slot:[`item.imagen`]="{ item }">
        <v-avatar size="40">
          <img :src="urlImagen(item.imagen)" alt="Foto" />
        </v-avatar>
      </template>

      <!-- Estado -->
      <template v-slot:[`item.estado`]="{ item }">
        <v-chip small :color="estado(item.estado)" text-color="white">
          {{ item.estado || 'SIN MEMBRESÍA' }}
        </v-chip>
      </template>

      <!-- Registrado por (solo admin) -->
      <template v-slot:[`item.registradoPor`]="{ item }">
        <v-chip x-small color="blue-grey lighten-4" class="text-caption">
          <v-icon x-small left>mdi-account-edit</v-icon>
          {{ item.registradoPor || 'Sistema' }}
        </v-chip>
      </template>

      <!-- Opciones -->
      <template v-slot:[`item.opciones`]="{ item }">
        <!-- Ver historial (todos) -->
        <v-tooltip bottom color="deep-purple">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="deep-purple" small fab dark v-bind="attrs" v-on="on" @click="verHistorial(item.id)">
              <v-icon>mdi-account-clock</v-icon>
            </v-btn>
          </template>
          <span>Ver historial</span>
        </v-tooltip>

        <!-- Editar (admin y recepcionista) -->
        <v-tooltip bottom color="primary" v-if="esAdmin || esRecepcionista">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="primary" small fab dark v-bind="attrs" v-on="on" @click="editar(item.id)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <span>Editar</span>
        </v-tooltip>

        <!-- Eliminar (solo admin) -->
        <v-tooltip bottom color="error" v-if="esAdmin">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="error" small fab dark v-bind="attrs" v-on="on" @click="eliminar(item)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>Eliminar</span>
        </v-tooltip>

        <!-- Credencial (admin y recepcionista) -->
        <v-tooltip bottom color="secondary" v-if="esAdmin || esRecepcionista">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="secondary" small fab dark v-bind="attrs" v-on="on" @click="generarCredencial(item)">
              <v-icon>mdi-card-account-details</v-icon>
            </v-btn>
          </template>
          <span>Credencial</span>
        </v-tooltip>

        <!-- QR del miembro -->
        <v-tooltip bottom color="teal">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <q-r-miembro :miembro="item">
                <template #activator="{ on: qrOn }">
                  <v-btn color="teal" small fab dark v-on="qrOn">
                    <v-icon>mdi-qrcode</v-icon>
                  </v-btn>
                </template>
              </q-r-miembro>
            </span>
          </template>
          <span>Ver QR</span>
        </v-tooltip>

        <!-- Pagar (todos) -->
        <v-tooltip bottom color="success" v-if="!item.estado || item.estado === 'VENCIDO'">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="success" small fab dark v-bind="attrs" v-on="on" @click="realizarPago(item.matricula)">
              <v-icon>mdi-wallet-membership</v-icon>
            </v-btn>
          </template>
          <span>Realizar pago</span>
        </v-tooltip>

        <!-- Cambiar membresía (todos los roles) -->
        <v-tooltip bottom color="orange darken-2">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="orange darken-2" small fab dark v-bind="attrs" v-on="on"
              @click="abrirCambioMembresia(item)">
              <v-icon>mdi-swap-horizontal</v-icon>
            </v-btn>
          </template>
          <span>Cambiar membresía</span>
        </v-tooltip>
      </template>

      <!-- Expansión con info adicional -->
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-simple-table dense class="my-2">
            <tbody>
              <tr>
                <td><v-icon color="indigo" small>mdi-map-marker</v-icon> <b>Dirección:</b> {{ item.direccion }}</td>
                <td><v-icon color="indigo" small>mdi-calendar</v-icon> <b>Miembro desde:</b> {{ item.fechaRegistro }}</td>
              </tr>
              <tr>
                <td><v-icon color="indigo" small>mdi-account</v-icon> <b>Contacto:</b> {{ item.nombreContacto }} — {{ item.telefonoContacto }}</td>
                <td><v-icon color="indigo" small>mdi-hospital-building</v-icon> <b>Seguro:</b> {{ item.institucion }}</td>
              </tr>
              <tr v-if="esAdmin">
                <td colspan="2">
                  <v-icon color="blue" small>mdi-account-edit</v-icon>
                  <b>Matriculado por:</b>
                  <v-chip x-small color="info" class="ml-1">{{ item.registradoPor || 'Sistema' }}</v-chip>
                  &nbsp;&nbsp;
                  <v-btn x-small color="deep-purple" dark rounded @click="verHistorial(item.id)">
                    <v-icon x-small left>mdi-history</v-icon> Ver historial completo
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </td>
      </template>
    </v-data-table>

    <!-- FAB nuevo miembro -->
    <v-btn fab dark x-large elevation="8" color="primary" fixed right bottom to="/nuevo-miembro">
      <v-icon dark>mdi-plus</v-icon>
    </v-btn>

    <!-- Dialog pago -->
    <v-dialog v-model="mostrarRealizarPago" persistent max-width="600">
      <realizar-pago
        :matricula="matriculaSeleccionada"
        @cerrar="cerrarDialogoPago"
        @pagado="onPagado"
      />
    </v-dialog>

    <!-- Dialog eliminar -->
    <v-dialog v-model="mostrarDialogoEliminar" max-width="500px">
      <dialogo-eliminar
        :nombre="itemSeleccionado"
        @cancelar="cerrarDialogoEliminar"
        @eliminar="confirmarEliminar"
      />
    </v-dialog>

    <!-- Credencial -->
    <credencial-miembro
      v-if="mostrarCredencial"
      :matricula="matriculaSeleccionada"
      :miembro="miembro"
      @impreso="onImpreso"
    />

    <!-- Dialog Cambio de Membresía -->
    <v-dialog v-model="mostrarCambioMembresia" max-width="420" persistent>
      <v-card rounded="xl">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          <v-icon color="orange darken-2" class="mr-2">mdi-swap-horizontal</v-icon>
          Cambiar membresía
        </v-card-title>
        <v-card-subtitle v-if="miembroCambio">{{ miembroCambio.nombre }}</v-card-subtitle>
        <v-card-text>
          <v-select
            v-model="membresiaSeleccionada"
            :items="membresias"
            item-text="nombre"
            item-value="id"
            label="Seleccionar membresía"
            outlined dense
            clearable
            hint="Deja vacío para quitar la membresía"
            persistent-hint
          >
            <template #item="{ item: m }">
              <span>{{ m.nombre }}</span>
              <v-spacer />
              <v-chip x-small color="primary" dark class="ml-2">{{ m.duracion }} días</v-chip>
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-btn text @click="mostrarCambioMembresia = false">Cancelar</v-btn>
          <v-spacer />
          <v-btn color="orange darken-2" dark rounded :loading="guardandoMembresia"
            @click="confirmarCambioMembresia">
            <v-icon left small>mdi-check</v-icon> Aplicar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="mostrarMensaje" :timeout="3000" :color="mensaje.color" top>
      {{ mensaje.texto }}
    </v-snackbar>

    <v-overlay :value="cargando">
      <v-progress-circular size="64" indeterminate />
    </v-overlay>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService'
import Utiles from '../../Servicios/Utiles'
import RealizarPago from './RealizarPago.vue'
import DialogoEliminar from '../Dialogos/DialogoEliminar.vue'
import CredencialMiembro from './CredencialMiembro.vue'
import QRMiembro from './QRMiembro.vue'

export default {
  name: 'Miembros',
  components: { RealizarPago, DialogoEliminar, CredencialMiembro, QRMiembro },

  data: () => ({
    cargando: false,
    mostrarMensaje: false,
    mensaje: { color: '', texto: '' },
    miembros: [],
    membresias: [],
    matriculaSeleccionada: '',
    mostrarRealizarPago: false,
    itemSeleccionado: null,
    miembro: null,
    mostrarDialogoEliminar: false,
    mostrarCredencial: false,
    mostrarCambioMembresia: false,
    miembroCambio: null,
    membresiaSeleccionada: null,
    guardandoMembresia: false,
    rolActual: localStorage.getItem('rolUsuario') || 'recepcionista',
  }),

  computed: {
    esAdmin()        { return this.rolActual === 'admin' },
    esRecepcionista(){ return this.rolActual === 'recepcionista' },

    encabezadoTabla() {
      const base = [
        { text: 'Imagen',        value: 'imagen',    sortable: false, width: 56 },
        { text: 'Matrícula',     value: 'matricula', sortable: true },
        { text: 'Nombre',        value: 'nombre',    sortable: true },
        { text: 'Teléfono',      value: 'telefono',  sortable: false },
        { text: 'Estado',        value: 'estado',    sortable: true },
        { text: 'Fin membresía', value: 'fechaFin',  sortable: true },
        { text: 'Membresía',     value: 'membresia', sortable: true },
      ]
      if (this.esAdmin) {
        base.push({ text: 'Matriculado por', value: 'registradoPor', sortable: false })
      }
      base.push({ text: 'Opciones', value: 'opciones', sortable: false })
      return base
    },
  },

  mounted() {
    this.obtenerMiembros()
    this.obtenerMembresias()
  },

  methods: {
    verHistorial(idMiembro) {
      this.$router.push({ name: 'HistorialMiembro', params: { id: idMiembro } })
    },

    generarCredencial(miembro) {
      this.matriculaSeleccionada = miembro.matricula
      this.miembro = miembro
      this.mostrarCredencial = true
    },

    onImpreso(resultado) {
      this.mostrarCredencial = resultado
    },

    eliminar(miembro) {
      this.itemSeleccionado = miembro.nombre
      this.miembro = miembro
      this.mostrarDialogoEliminar = true
    },

    cerrarDialogoEliminar() {
      this.mostrarDialogoEliminar = false
      this.miembro = null
    },

    confirmarEliminar() {
      this.cargando = true
      HttpService.eliminar('miembros.php', { metodo: 'eliminar', id: this.miembro.id }).then(eliminado => {
        if (eliminado) {
          this.mostrarDialogoEliminar = false
          this.mensaje = { color: 'success', texto: 'Miembro eliminado' }
          this.mostrarMensaje = true
          this.cargando = false
          this.obtenerMiembros()
        }
      })
    },

    editar(idMiembro) {
      this.$router.push({ name: 'EditarMiembro', params: { id: idMiembro } })
    },

    urlImagen(imagen) {
      return Utiles.generarURL(imagen)
    },

    cerrarDialogoPago(resultado) {
      this.mostrarRealizarPago = resultado
    },

    realizarPago(matricula) {
      this.matriculaSeleccionada = matricula
      this.mostrarRealizarPago = true
    },

    onPagado(resultado) {
      if (resultado) {
        this.mostrarRealizarPago = false
        this.mensaje = { color: 'success', texto: 'Pago realizado con éxito ✓' }
        this.mostrarMensaje = true
        this.obtenerMiembros()
      }
    },

    obtenerMiembros() {
      this.cargando = true
      HttpService.obtenerConDatos({ metodo: 'get' }, 'miembros.php').then(resultado => {
        this.miembros = resultado || []
        this.cargando = false
      })
    },

    obtenerMembresias() {
      HttpService.obtenerConDatos({ metodo: 'get' }, 'membresias.php').then(r => {
        this.membresias = r || []
      })
    },

    abrirCambioMembresia(item) {
      this.miembroCambio       = item
      this.membresiaSeleccionada = item.idMembresia || null
      this.mostrarCambioMembresia = true
    },

    confirmarCambioMembresia() {
      if (!this.miembroCambio) return
      this.guardandoMembresia = true
      HttpService.registrar({
        metodo: 'cambiar_membresia',
        idMiembro:   this.miembroCambio.id,
        idMembresia: this.membresiaSeleccionada || null,
      }, 'miembros.php').then(r => {
        this.guardandoMembresia   = false
        this.mostrarCambioMembresia = false
        if (r && r.ok) {
          this.mensaje = { color: 'success', texto: 'Membresía actualizada ✓' }
          this.obtenerMiembros()
        } else {
          this.mensaje = { color: 'error', texto: 'Error al actualizar membresía' }
        }
        this.mostrarMensaje = true
      })
    },

    estado(val) {
      return val === 'ACTIVO' ? 'success' : val === 'VENCIDO' ? 'error' : 'warning'
    },
  },
}
</script>


// WEBPACK FOOTER //
// src/components/Miembros/Miembros.vue